import { EntityRepository } from '@record-it-npm/synchro-client';
import { EntityName } from 'record-it-time-common/EntityName';
import { assertNotNullOrUndefined } from 'record-it-utils/utils/assert/assertNotNullOrUndefined';
import { AppSynchronizationEnvironmentTypes } from '../../../AppSynchronizationEnvironmentTypes';
import { AppEntityManagerEntityTypesByEntityName } from '../../entities/AppEntityManagerEntityTypesByEntityName';

/**
 * A container for an entity, which will always return the newest value of it from the db.
 *
 * This is needed, so you don't have stale id's in asynchronous tasks.
 *
 * This also should be used if you pass the entity as an callback (of an component) parameter, so the parent component doesn't have to handle this.
 *
 *
 * @performance - Performance could be increased, by having a counter in the repository for each change of any entity.
 * Then we can locally cache the counter and entity here. If the counter is the same, we don't have request the entity each time.
 */
export class LatestEntityContainer<TEntityName extends EntityName> {
  private latestId: string;
  private readonly repository: EntityRepository<AppSynchronizationEnvironmentTypes, TEntityName, any>; // any typing for performance reasons

  constructor(entityId: string, repository: EntityRepository<AppSynchronizationEnvironmentTypes, TEntityName, any>) {
    this.latestId = entityId;
    this.repository = repository;
  }
  public getLatest(): AppEntityManagerEntityTypesByEntityName[TEntityName]['entity'] {
    const entity = this.getOptionalLatest();
    assertNotNullOrUndefined(entity, `no ${this.repository.getEntityInfo().entityName} entity found for id "${this.latestId}"`);
    return entity;
  }
  public getOptionalLatest(): AppEntityManagerEntityTypesByEntityName[TEntityName]['entity'] | null {
    let entity = this.repository.getById(this.latestId);
    if (!entity) {
      entity = this.repository.getByOriginalId(this.latestId);
      if (entity) {
        // update the currentId, since the id got upgraded.
        // this improves performance a little bit, since in the future it will succeed on the first call
        this.latestId = entity.id;
      }
    }
    return entity;
  }
}